import { PATIENT_ROSTER_OUTPUT_FIELDS } from './patientRosterColumns';
import type {
  DatasetManifestEntry,
  ReportDefinition,
} from '../types/reporting';

/** Whether the definition uses the persisted explicit-selection contract. */
export function usesCustomDetailColumns(def: ReportDefinition): boolean {
  // Generic schema v4 stores an explicit selected-column list alongside
  // display groups. Patient Roster v4 is a fixed-output pipeline, so its empty
  // columns list keeps the legacy pipeline meaning.
  return (
    def.detail_columns_mode === 'custom' ||
    (def.schema_version === 4 && def.pipeline?.type !== 'patient_roster')
  );
}

/**
 * Legacy empty lists and v3 `all` emit every primary dataset column. V3 custom
 * and v4 lists are literal, including an intentional empty list.
 */
export function effectiveDetailColumns(
  def: ReportDefinition,
  dataset: DatasetManifestEntry
): string[] {
  if (def.pipeline?.type === 'patient_roster') {
    // Patient Roster is a fixed-output pipeline. A v4 display_columns list makes
    // an empty columns array intentional (Clear all); older definitions with no
    // display metadata retain the legacy all-fields meaning.
    const selected =
      def.display_columns !== undefined ||
      def.detail_columns_mode === 'custom' ||
      def.schema_version === 4
        ? def.columns
        : def.columns.length > 0
          ? def.columns
          : PATIENT_ROSTER_OUTPUT_FIELDS;
    const allowed = new Set(PATIENT_ROSTER_OUTPUT_FIELDS);
    return selected.filter(field => allowed.has(field));
  }
  return usesCustomDetailColumns(def)
    ? def.columns
    : def.columns.length > 0
      ? def.columns
      : dataset.columns.map(column => column.name);
}

export function hasCustomEmptyDetailColumns(def: ReportDefinition): boolean {
  return usesCustomDetailColumns(def) && def.columns.length === 0;
}

/**
 * A detail-column edit is explicit. Legacy definitions move to v3; v4 keeps its
 * display-column contract and omits the v3-only detail_columns_mode field.
 */
export function withCustomDetailColumns(
  def: ReportDefinition,
  columns: string[]
): ReportDefinition {
  if (def.schema_version === 4 || def.display_columns !== undefined) {
    const next: ReportDefinition = { ...def, schema_version: 4, columns };
    delete next.detail_columns_mode;
    return next;
  }
  return {
    ...def,
    schema_version: 3,
    detail_columns_mode: 'custom',
    columns,
  };
}
